import {
    ChangeDetectionStrategy,
    Component,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    AppStateFacade,
    Setting,
} from '@app-state';
import {
    UiSettingForm,
    UiSkippedDaysSettingsComponent,
} from '@components';
import {
    ComponentWithForm,
    FeatureFlag,
    IExcludedCalendar,
    ISettings,
    ModalResultType,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import { MailModalComponent } from '@pages/settings/mail.modal';
import {
    ModalService,
    SettingService,
} from '@services';
import { CalendarService } from '@services/calendar.service';
import { UiSnackBarService } from '@uipath/angular/components/ui-snackbar';

import cloneDeep from 'lodash-es/cloneDeep';
import * as moment from 'moment';
import {
    BehaviorSubject,
    combineLatest,
    forkJoin,
    of,
} from 'rxjs';
import {
    finalize,
    map,
    switchMap,
    tap,
} from 'rxjs/operators';

const EMPTY_SETTING_FORM = {
    formGroup: {},
} as UiSettingForm;

const enum SmtpTestResult {
    // Initial state after mail settings are loaded
    ASIS = '',
    // Mail settings have changed, but not tested
    NotTested = 'settings_mail_not_tested',
    // Mail settings have been tested successfully
    Succeeded = 'settings_mail_test_succeeded',
    // Mail settings have been tested, but failed
    Failed = 'settings_mail_test_failed',
}

export enum TabLabel {
    General = 'General',
    Deployment = 'Deployment',
    Mail = 'Mail',
    Security = 'Security',
    Scalability = 'Scalability',
    NonWorkingDays = 'Non-Working Days',
}

@Component({
    templateUrl: './settings.page.html',
    styleUrls: ['../shared/settings.common.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class SettingsPageComponent implements OnInit, ComponentWithForm {

    @ViewChild('general', { static: false })
    public set general(ref: UiSettingForm) {
        if (!this._forms['general'] && !!ref) {
            this._forms['general'] = ref;
        }
    }
    public get general() {
        return this._forms['general'] || EMPTY_SETTING_FORM;
    }
    @ViewChild('deployment', { static: false })
    public set deployment(ref: UiSettingForm) {
        if (!this._forms['deployment'] && !!ref) {
            this._forms['deployment'] = ref;
        }
    }
    public get deployment() {
        return this._forms['deployment'] || EMPTY_SETTING_FORM;
    }
    @ViewChild('mail', { static: false })
    public set mail(ref: UiSettingForm) {
        if (!this._forms['mail'] && !!ref) {
            this._forms['mail'] = ref;
        }
    }
    public get mail() {
        return this._forms['mail'] || EMPTY_SETTING_FORM;
    }
    @ViewChild('security', { static: false })
    public set security(ref: UiSettingForm) {
        if (!this._forms['security'] && !!ref) {
            this._forms['security'] = ref;
        }
    }
    public get security() {
        return this._forms['security'] || EMPTY_SETTING_FORM;
    }
    @ViewChild('scalability', { static: false })
    public set scalability(ref: UiSettingForm) {
        if (!this._forms['scalability'] && !!ref) {
            this._forms['scalability'] = ref;
        }
    }
    public get scalability() {
        return this._forms['scalability'] || EMPTY_SETTING_FORM;
    }

    @ViewChild('skippedDays', { static: false })
    public set skippedDays(ref: UiSettingForm) {
        if (!this._forms['skippedDays'] && !!ref) {
            this._forms['skippedDays'] = ref;
        }
    }
    public get skippedDays() {
        return this._forms['skippedDays'] || EMPTY_SETTING_FORM;
    }

    public get invalid(): boolean {
        return this.general.formGroup.invalid ||
            this.deployment.formGroup.invalid ||
            this.mail.formGroup.invalid ||
            this.security.formGroup.invalid ||
            this.scalability.formGroup.invalid ||
            (!this.isMultipleCalendarsEnabled && this.skippedDays.formGroup.invalid);
    }
    public get pristine(): boolean {
        return this.general.formGroup.pristine &&
            this.deployment.formGroup.pristine &&
            this.mail.formGroup.pristine &&
            this.security.formGroup.pristine &&
            this.scalability.formGroup.pristine &&
            (this.isMultipleCalendarsEnabled || this.skippedDays.formGroup.pristine);
    }

    public get hasUnsavedChanges(): boolean {
        return !this.pristine;
    }
    public loading$ = new BehaviorSubject(true);
    public settings!: ISettings;
    public creatingCalendar$ = new BehaviorSubject(false);
    public excludedCalendars?: IExcludedCalendar[];
    public selectedCalendarIndex = 0;
    public activeTab = 'General';
    public permission = 'Settings.Edit';
    public isWindowsAuthEnabled?: boolean;
    public isMultipleCalendarsEnabled?: boolean;
    public TabLabel = TabLabel;

    private _smtpTestResult = SmtpTestResult.NotTested;

    private _forms: { [key: string]: UiSettingForm } = {};

    public get hasInvalidCalendars() {
        const skippedDaysComponent = this.skippedDays as UiSkippedDaysSettingsComponent;
        return skippedDaysComponent &&
            skippedDaysComponent.calendarItems &&
            skippedDaysComponent.calendarItems.some(item => item.formGroup.invalid);
    }

    public get hasUnsavedCalendars() {
        const skippedDaysComponent = this.skippedDays as UiSkippedDaysSettingsComponent;
        return skippedDaysComponent &&
            skippedDaysComponent.calendarItems &&
            skippedDaysComponent.calendarItems.some(item => item.formGroup.dirty);
    }

    constructor(
        public settingService: SettingService,
        private _calendarService: CalendarService,
        private _translateService: TranslateService,
        private _modalService: ModalService,
        private _snack: UiSnackBarService,
        private _appState: AppStateFacade,
    ) { }

    ngOnInit() {
        this.isWindowsAuthEnabled = this._appState.settings.get(Setting.isWindowsAuthEnabled);
        this.isMultipleCalendarsEnabled = this._appState.settings.isFeatureEnabled(FeatureFlag.MultipleNonWorkingCalendars);

        const calendars$ = this.isMultipleCalendarsEnabled ?
            this._calendarService.get({ orderBy: ['Name'] }) :
            this.settingService.getCalendar()
                .pipe(
                    map((calendar) => ({
                        total: 1,
                        data: [calendar],
                    })),
                );

        forkJoin({
            settings: this.settingService.getServerSettings(),
            calendars: calendars$,
        }).pipe(
            finalize(() => this.loading$.next(false)),
        ).subscribe(
            ({ settings, calendars }) => {
                this.settings = settings;
                this.excludedCalendars = calendars.data || [];
            },
        );
    }

    public openModal() {
        // SMTP password is not provided by backend. A dummy one is provided instead.
        // Needs customer to provide a password. Otherwise, the test will always fail even if no settings are changed.
        if (this.settings.mail.smtpPassword === '••••••••••••') {
            this._snack.warning(this._translateService.instant('settings_mail_needs_password_warning'));
            return;
        }

        this._modalService.openModal(
            MailModalComponent,
            this.settings.mail,
        )
            .afterClosedResult()
            .subscribe(res => {
                switch (res.type) {
                    case ModalResultType.Succeeded:
                        this._smtpTestResult = SmtpTestResult.Succeeded;
                        this._snack.success(this._translateService.instant('settings_mail_test_succeeded', {
                            sendTo: res.additionalData,
                        }));
                        break;
                    case ModalResultType.Failed:
                        this._smtpTestResult = SmtpTestResult.Failed;
                        break;
                }
            });
    }

    public onSubmit() {
        const enableFolders = this.general.formGroup.get('isModernFolderSystemEnabled')!;
        const enableFoldersDirty = enableFolders.dirty;

        if (!enableFoldersDirty || !this.settings.general.isModernFolderSystemEnabled) {
            this._checkMailSettings();
        } else {
            this._modalService.onConfirm(
                null,
                () => this._checkMailSettings(),
                () => {
                    enableFolders.setValue(this.settings.general.isModernFolderSystemEnabled = false);
                    this._checkMailSettings();
                },
                this._translateService.instant('settings_general_enable_modern_sure'),
                true,
            );
        }
    }

    public onSmtpSettingChanged(smtpSettingChanged: boolean) {
        this._smtpTestResult = smtpSettingChanged ? SmtpTestResult.NotTested : SmtpTestResult.ASIS;
    }

    public getMailSettingsConfirmationMessage(smtpTestResult: SmtpTestResult) {
        switch (smtpTestResult) {
            case SmtpTestResult.Failed:
                return 'settings_mail_test_failed_confirmation';
            case SmtpTestResult.NotTested:
                return 'settings_mail_not_tested_confirmation';
            case SmtpTestResult.ASIS:
            case SmtpTestResult.Succeeded:
                return null;
            default:
                throw new Error('Unknown SMTP test result: ' + smtpTestResult);
        }
    }

    public saveSettings() {
        this.loading$.next(true);

        const payload = cloneDeep(this.settings);

        const selectedCalendar = this.excludedCalendars![this.selectedCalendarIndex];
        if (!this.isMultipleCalendarsEnabled && this.skippedDays.formGroup.dirty) {
            selectedCalendar.excludedDates = this.skippedDays.formGroup.get('date')!
                .value
                .map((d: moment.MomentInput) => moment(d).toDate());
        }

        const updateCalendar$ = !this.skippedDays.formGroup.dirty || this.isMultipleCalendarsEnabled ?
            of(null) :
            this.settingService.updateCalendar(selectedCalendar);

        combineLatest([
            this.settingService
                .updateSettingsBulk(payload, {
                    'General': this.general.formGroup.dirty,
                    'Deployment': this.deployment.formGroup.dirty,
                    'Mail': this.mail.formGroup.dirty,
                    'Security': this.security.formGroup.dirty,
                    'Scalability': this.scalability.formGroup.dirty,
                }),
            updateCalendar$,
        ])
            .pipe(
                switchMap(() => this._appState.settings.fetch()),
                tap(() => {
                    this._snack.success(this._translateService.instant('settings_save_success_message'));

                    this.general.formGroup.reset(this.general.formGroup.value);
                    this.deployment.formGroup.reset(this.deployment.formGroup.getRawValue());
                    this.mail.formGroup.reset(this.mail.formGroup.value);
                    this.security.formGroup.reset(this.security.formGroup.value);
                    this.scalability.formGroup.reset(this.scalability.formGroup.getRawValue());

                    if (!this.isMultipleCalendarsEnabled) {
                        this.skippedDays.formGroup.reset(this.skippedDays.formGroup.value);
                    }
                }),
                switchMap(() => this.settingService.getServerSettings()),
                finalize(() => this.loading$.next(false)),
            )
            .subscribe((newSettings) => this.settings = newSettings);
    }

    public selectionChange(index: number) {
        this.selectedCalendarIndex = index;
    }

    private _checkMailSettings() {
        const mailSettingsConfirmationMessage = this.getMailSettingsConfirmationMessage(this._smtpTestResult);

        if (!this.mail.formGroup.dirty || mailSettingsConfirmationMessage == null) {
            this.saveSettings();
        } else {
            this._modalService.onConfirm(
                null,
                () => this.saveSettings(),
                () => { },
                this._translateService.instant(mailSettingsConfirmationMessage),
            );
        }
    }
}
